import CompFinalConverter from '@/converters/2.6/CompFinalConverter';
import IComps from '@/models/comp/IComps';
import IUserSettings from '@/models/IUserSettings';
import HostExchangeService from '@/services/2.6/HostExchangeService';
import { store, Getters, mlsStore, salesHistoryStore, userSettingsStore } from '../../store';
import _ from 'lodash';
import CompUtils from '@/models/comp/CompUtils';
import theLocalStorageUtils from '@/utils/LocalStorageUtils';
import { LocalStorageKeys } from '@/constants/LocalStorageKeys';
import CompFinalDataUtils from '@/models/comp-final/CompFinalDataUtils';
import { SourceDataInitials } from '@/constants/SourceDataInitials';
import IComp from '@/models/Comp';
import LogUsageManager from '../LogUsageManager';
import ICompData from '@/models/comp/ICompData';
import CompDataUtils from '@/models/comp/CompDataUtils';
import FactSheetManager from './FactSheetManager';
import SalesHistoryManager from './SalesHistoryManager';
import IExchangeReportFields from '@/models/exchange/IExchangeReportFields';
import IExchangeReportField from '@/models/exchange/IExchangeReportField';
import { FieldNames } from '@/constants/FieldNames';
import OriginalValuesManager from './OriginalValuesManager';
import QSUtils from '@/utils/QSUtils';
import ICompFinalData from '@/models/comp-final/ICompFinalData';
// import TimingsUtil from '@/utils/TimingsUtil';

const waitTimeMillis = 200;
const maxAvailableAttempts = 20;

export default {
    async waitOnAvailability(): Promise<boolean> {
        const hostExchangeService = HostExchangeService.getInstance();

        let attempts = 0;
        let available = false;
        do {
            if (attempts > 0) {
                await QSUtils.sleep(waitTimeMillis);
            }

            available = await hostExchangeService.isAvailable();
            attempts++;
        } while (!available && attempts < maxAvailableAttempts);

        if (available) {
            if (attempts > 1) {
                console.log(`HostExchangeManager.waitOnAvailability(), Available after [${attempts}] attempts, wait time[${waitTimeMillis}(ms)].`);
            } else {
                console.log(`HostExchangeManager.waitOnAvailability(), Available on first attempt.`);
            }
        } else {
            console.log(`HostExchangeManager.waitOnAvailability(), Not available after [${attempts}] attempts, wait time[${waitTimeMillis}(ms)].`);
        }

        return available;
    },

    /**
     * Sends all data required by our host service. Pulls various data from the store. Manages
     * updating, converting and sending.
     */
    async sendAll() {
        if(QSUtils.debugger()) {
            console.log("sendAll");
        }

        const comps: IComps = store.getters[Getters.CompAddresses.Comps];
        const userSettings: IUserSettings = userSettingsStore.UserSettings!;
        if(QSUtils.debugger()) {
            console.log("comps result: ", comps);
            console.log("userSettings result: ", userSettings);
        }

        const hostExchangeService = HostExchangeService.getInstance();

        //const timings = new TimingsUtil();
        // timings.addMark('SendAll_Start');

        // Comps data
        const compsToExport = getCompsToExport(comps, userSettings);
        if(QSUtils.debugger()) {
            console.log("compsToExport result: ", compsToExport);
        }

        const compFinalData = CompFinalConverter.convert(compsToExport, userSettings);
        if(QSUtils.debugger()) {
            console.log("CompFinalConverter result: ", compFinalData);
        }


        // timings.addMark('ConvertedComps');

        // Export final comps
        const finalCompsPromise = sendFinalCompsAndReportFieldData(compFinalData);

        if(QSUtils.debugger()) {
            console.log("sendFinalCompsAndReportFieldData completed");
        }

        const fileContentPromise = sendFileContent(compFinalData);

        if(QSUtils.debugger()) {
            console.log("sendFileContent completed");
        }

        // Work File data
        const workFile = await FactSheetManager.createFromCompFinalData(compFinalData);
        if(QSUtils.debugger()) {
            console.log("workFile result: ", workFile);
        }

        const dataFactSheetPromise = hostExchangeService.placeDataFactSheetInWorkfile(workFile);

        const promiseGroupResults = await Promise.all([finalCompsPromise, fileContentPromise, dataFactSheetPromise]);
        // timings.addMark('Group');

        // timings.doMeasures(true);
        // timings.logMeasures('HostExchangeManager.sendAll()');
    },

    /**
     * Use the active comp and any corresponding MLS property data to create a fact sheet.
     * Send it to our host for launching in a browser.
     */
    async requestActiveCompFactSheet() {
        const activeComp: IComp = store.getters[Getters.CompAddresses.ActiveComp];
        if (activeComp) {
            const factSheet = await FactSheetManager.createFromComp(activeComp);

            const service = HostExchangeService.getInstance();
            service.sendDataFactSheet(factSheet, activeComp.isSubject ?? false); // No await
        }
    },

    /**
     * Notify host it's time to close the application. Log a message when saved is false.
     */
    async closeApp(saved: boolean) {
        if (!saved) {
            await LogUsageManager.closedWithoutSave();
        }

        const hostExchangeService = HostExchangeService.getInstance();
        await hostExchangeService.closeForm();
    },
};

/**
 * Get an array of the comps to export. Checks the given comps for isSubject, hasVisited and
 * export all comps from user settings.
 * Clones each comp to be exported so there are no reactive side effects.
 * @param comps
 * @param userSettings
 * @returns
 */
function getCompsToExport(comps: IComps, userSettings: IUserSettings): IComps {
    const compsToExport: IComps = [];
    for (const comp of comps) {
        if (userSettings.exportAllComps || CompUtils.shouldExport(comp)) {
            const compToExport = _.cloneDeep(comp);
            compsToExport.push(compToExport);
        }

        // When not exporting all comps and an exporting comp hasn't been visited
        // we need to revert transfer values to only those marked as include.
        if (!userSettings.exportAllComps) {
            for (const comp of compsToExport) {
                if (!comp.hasVisited) {
                    revertSourceDataToProvidedValues(comp);
                }
            }
        }
    }
    return compsToExport;
}

function revertSourceDataToProvidedValues(comp: IComp) {
    const compDatas: ICompData[] = [];

    if (comp.sourceData) {
        for (const compData of comp.sourceData) {
            if (compData.include === '1' || compData.sourceInitial === SourceDataInitials.FormFiller || CompDataUtils.isAddressField(compData)) {
                compDatas.push(compData);
            }
        }

        comp.sourceData = compDatas;
    }
}

async function sendFinalCompsAndReportFieldData(compFinalData: ICompFinalData): Promise<void> {
    if(QSUtils.debugger()) {
        console.log("sendFinalCompsAndReportFieldData method");
    }

    const hostExchangeService = HostExchangeService.getInstance();

    await hostExchangeService.exportFinalComps(compFinalData);
    if(QSUtils.debugger()) {
        console.log("exportFinalComps completed");
    }

    await logExported(compFinalData);
    if(QSUtils.debugger()) {
        console.log("logExported completed");
    }

    // Report Field data
    const reportFields: IExchangeReportFields = [];

    // Handle analysis of prior sales
    const priorSalesAnalyses = await SalesHistoryManager.generateAnalyses(compFinalData);
    if(QSUtils.debugger()) {
        console.log("priorSalesAnalyses result: ", priorSalesAnalyses);
    }
    if (priorSalesAnalyses.length > 0) {
        const reportField: IExchangeReportField = {
            fieldName: FieldNames.AnalysisOfPriorSale,
            value: priorSalesAnalyses,
        };
        reportFields.push(reportField);
    }

    // Handle any sales histories marked for the addenda
    const salesHistoryProperties = await SalesHistoryManager.getForAddenda(compFinalData);
    if(QSUtils.debugger()) {
        console.log("salesHistoryProperties result: ", salesHistoryProperties);
    }
    if (salesHistoryProperties.length > 0) {
        const reportField: IExchangeReportField = {
            fieldName: FieldNames.AddendaWithSalesHistories,
            value: salesHistoryProperties,
        };
        reportFields.push(reportField);
    }

    if(QSUtils.debugger()) {
        console.log("reportFields result: ", reportFields);
    }

    // There may or may not be report fields to send
    if (reportFields.length > 0) {
        await hostExchangeService.pushReportFieldDataToReport(reportFields);
    }
}

async function logExported(compFinalData: ICompFinalData): Promise<void> {
    if(QSUtils.debugger()) {
        console.log("logExported method");
    }

    const quickSourceDataPromise = LogUsageManager.exportedQuickSourceData(compFinalData.length - 1); // Don't count the subject property
    if(QSUtils.debugger()) {
        console.log("quickSourceDataPromise result: ", quickSourceDataPromise);
    }

    const seExists = CompFinalDataUtils.containsTransferValueFromSource(compFinalData, SourceDataInitials.SmartExchange);
    if(QSUtils.debugger()) {
        console.log("seExists result: ", seExists);
    }
    const smartExchangeDataPromise = LogUsageManager.exportedSmartExchangeData(seExists);

    const prExists = CompFinalDataUtils.containsTransferValueFromSource(compFinalData, SourceDataInitials.PublicRecords);
    if(QSUtils.debugger()) {
        console.log("prExists result: ", prExists);
    }
    const publicRecordsPromise = LogUsageManager.exportedPublicRecordsData(prExists);

    const mlsExists = CompFinalDataUtils.containsTransferValueFromSource(compFinalData, SourceDataInitials.Mls);
    if(QSUtils.debugger()) {
        console.log("mlsExists result: ", mlsExists);
    }
    const transferValueFromSourcePromise = LogUsageManager.exportedMlsData(mlsExists);

    const dbExists = CompFinalDataUtils.containsTransferValueFromSource(compFinalData, SourceDataInitials.Database);
    if(QSUtils.debugger()) {
        console.log("dbExists result: ", dbExists);
    }
    const localDatabasePromise = LogUsageManager.exportedLocalCompDBData(dbExists);

    await Promise.all([quickSourceDataPromise, smartExchangeDataPromise, publicRecordsPromise, transferValueFromSourcePromise, localDatabasePromise]);
}

async function sendFileContent(compFinalData: ICompFinalData): Promise<void> {

    if(QSUtils.debugger()) {
        console.log("sendFileContent method");
    }

    const hostExchangeService = HostExchangeService.getInstance();
    // MLS data
    const mlsPropertyData = mlsStore.MlsProperties;
    if(QSUtils.debugger()) {
        console.log("mlsPropertyData result: ", mlsPropertyData);
    }
    const mlsDataPromise = hostExchangeService.putMlsDataInReport(mlsPropertyData);

    // CSV file info
    const csvFileInfos = mlsStore.CsvFileInfos;
    if(QSUtils.debugger()) {
        console.log("csvFileInfos result: ", csvFileInfos);
    }
    const mlsCsvInfoPromise = hostExchangeService.pushMlsCsvInfoToReport(csvFileInfos);

    // Original Values
    const compAddresses = CompFinalDataUtils.getCompAddresses(compFinalData);
    if(QSUtils.debugger()) {
        console.log("compAddresses result: ", compAddresses);
    }
    const originalValuesData = OriginalValuesManager.getOriginalValuesDataByAddresses(compAddresses);
    if(QSUtils.debugger()) {
        console.log("originalValuesData result: ", originalValuesData);
    }
    const originalValuesPromise = hostExchangeService.putOriginalValuesInReport(originalValuesData);

    // SalesHistorySelections
    const salesHistorySelectionsArray = salesHistoryStore.SalesHistorySelections;
    if(QSUtils.debugger()) {
        console.log("salesHistorySelectionsArray result: ", salesHistorySelectionsArray);
    }
    const salesHistorySelectionsPromise = hostExchangeService.putSalesHistorySelectionsInReport(salesHistorySelectionsArray);

    await Promise.all([mlsDataPromise, mlsCsvInfoPromise, originalValuesPromise, salesHistorySelectionsPromise]);
}
